/**
 * Post-match damage-meter table (#471) — pure row-building helper.
 *
 * Joins the roster (seat → championId, from `matchPhase`) with the
 * `damageDealt` totals (seat → total, also from `matchPhase`, only
 * populated at `phase === "ended"`) into display rows, sorted DESCENDING
 * by damage dealt (standard damage-meter convention). A seat present in
 * the roster but absent from `damageDealt` (shouldn't happen post-#471,
 * but kept defensive) defaults to 0, never `undefined`.
 */

import type { WireDamageEntry, WireRosterEntry } from "@pit/protocol";
import { ALL_CONTENT_CHAMPIONS } from "../champions/catalog.js";

export interface DamageMeterRow {
  readonly seat: number;
  readonly championId: string;
  readonly championName: string;
  readonly damageDealt: number;
  readonly isOwnSeat: boolean;
}

/** Champion display name for a content champion id, falling back to the
 *  raw id if the catalog somehow doesn't know it (never expected in
 *  practice — every seat's championId is server-validated content). */
function championName(championId: string): string {
  const found = ALL_CONTENT_CHAMPIONS.find((c) => c.id === championId);
  return found?.name ?? championId;
}

export function buildDamageMeterRows(
  roster: readonly WireRosterEntry[],
  damageDealt: readonly WireDamageEntry[],
  ownSeat: number | null,
): readonly DamageMeterRow[] {
  const bySeat = new Map<number, number>();
  for (const d of damageDealt) bySeat.set(d.seat, d.damageDealt);

  const rows: DamageMeterRow[] = roster.map((r) => ({
    seat: r.seat,
    championId: r.championId,
    championName: championName(r.championId),
    damageDealt: bySeat.get(r.seat) ?? 0,
    isOwnSeat: ownSeat !== null && r.seat === ownSeat,
  }));

  return rows.sort((a, b) => {
    if (a.damageDealt !== b.damageDealt) return b.damageDealt - a.damageDealt;
    return a.seat - b.seat; // stable tie-break: seat-ascending
  });
}
